import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { useBillingItems, formatNaira } from "@/hooks/use-supabase-queries";
import { useMatter, useMatterPartyNames, getPartyName } from "@/hooks/use-supabase-queries";
import { format } from "date-fns";

interface BillingInvoiceProps {
  matterId: string;
}

export function BillingInvoice({ matterId }: BillingInvoiceProps) {
  const { data: items, isLoading: billingLoading } = useBillingItems(matterId);
  const { data: matter, isLoading: matterLoading } = useMatter(matterId);
  const { data: partyNames } = useMatterPartyNames(matterId);

  if (billingLoading || matterLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const activeItems = items?.filter(i => i.status !== 'waived') || [];
  const totalAmount = activeItems.reduce((sum, i) => sum + i.amount, 0);
  const paidAmount = activeItems.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const balance = totalAmount - paidAmount;
  const clientName = getPartyName(partyNames, matter?.client_id || null);
  const lawyerName = getPartyName(partyNames, matter?.lawyer_id || null);

  const handlePrint = () => {
    const printContent = document.getElementById('invoice-printable');
    if (!printContent) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const { document: printDocument } = win;
    const title = printDocument.createElement("title");
    title.textContent = `Invoice - ${matter?.matter_number || "PropTRUST"}`;
    const style = printDocument.createElement("style");
    style.textContent = `
      body { font-family: system-ui, -apple-system, sans-serif; padding: 40px; color: #1a1a1a; max-width: 800px; margin: 0 auto; }
      .header { border-bottom: 3px solid #1a5632; padding-bottom: 20px; margin-bottom: 30px; }
      .brand { font-size: 24px; font-weight: 800; color: #1a5632; }
      .subtitle { color: #666; font-size: 12px; margin-top: 4px; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
      .info-block label { display: block; font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; }
      .info-block p { margin: 2px 0 0; font-weight: 500; }
      table { width: 100%; border-collapse: collapse; margin: 20px 0; }
      th { background: #f5f5f5; text-align: left; padding: 10px; font-size: 12px; text-transform: uppercase; color: #666; border-bottom: 2px solid #ddd; }
      td { padding: 10px; border-bottom: 1px solid #eee; font-size: 14px; }
      .amount { text-align: right; font-weight: 600; }
      .status { font-size: 11px; padding: 2px 8px; border-radius: 10px; }
      .paid { background: #e8f5e9; color: #2e7d32; }
      .pending { background: #fff3e0; color: #e65100; }
      .totals { margin-top: 20px; text-align: right; }
      .totals .row { display: flex; justify-content: flex-end; gap: 40px; padding: 6px 0; }
      .totals .total { font-size: 18px; font-weight: 700; border-top: 2px solid #1a5632; padding-top: 10px; color: #1a5632; }
      .footer { margin-top: 40px; border-top: 1px solid #ddd; padding-top: 20px; font-size: 11px; color: #999; text-align: center; }
      @media print { body { padding: 20px; } }
    `;
    printDocument.head.append(title, style);
    printDocument.body.replaceChildren(printContent.cloneNode(true));
    win.document.close();
    win.print();
  };

  return (
    <div className="space-y-4">
      <div id="invoice-printable">
        {/* Letterhead */}
        <div className="border-b-2 border-primary pb-4 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-extrabold text-primary">PropTRUST</h2>
              <p className="text-xs text-muted-foreground">Nigerian Property Transaction Platform</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-foreground">INVOICE</p>
              <p className="text-sm text-muted-foreground">Date: {format(new Date(), 'MMMM d, yyyy')}</p>
            </div>
          </div>
        </div>

        {/* Client & Matter Info */}
        <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Bill To</p>
            <p className="font-semibold text-foreground">{clientName}</p>
          </div>
          <div className="space-y-1 text-right">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Matter</p>
            <p className="font-semibold text-foreground">{matter?.matter_number}</p>
            <p className="text-muted-foreground">{matter?.property_address}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Prepared By</p>
            <p className="font-medium text-foreground">{lawyerName}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Property Value</p>
            <p className="font-medium text-foreground">{formatNaira(matter?.property_value)}</p>
          </div>
        </div>

        {/* Items Table */}
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left p-2 text-xs text-muted-foreground uppercase">Description</th>
              <th className="text-left p-2 text-xs text-muted-foreground uppercase">Category</th>
              <th className="text-right p-2 text-xs text-muted-foreground uppercase">Amount</th>
              <th className="text-right p-2 text-xs text-muted-foreground uppercase">Status</th>
            </tr>
          </thead>
          <tbody>
            {activeItems.map((item) => (
              <tr key={item.id} className="border-b border-border">
                <td className="p-2 font-medium text-foreground">{item.description}</td>
                <td className="p-2 text-muted-foreground capitalize">{item.category?.replace('-', ' ') || '-'}</td>
                <td className="p-2 text-right font-semibold text-foreground">{formatNaira(item.amount)}</td>
                <td className="p-2 text-right">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${item.status === 'paid' ? 'bg-primary/10 text-primary' : 'bg-accent/20 text-accent-foreground'}`}>
                    {item.status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-4 space-y-1 text-right text-sm">
          <div className="flex justify-end gap-8">
            <span className="text-muted-foreground">Total:</span>
            <span className="font-semibold text-foreground w-32">{formatNaira(totalAmount)}</span>
          </div>
          <div className="flex justify-end gap-8">
            <span className="text-muted-foreground">Paid:</span>
            <span className="font-semibold text-primary w-32">{formatNaira(paidAmount)}</span>
          </div>
          <div className="flex justify-end gap-8 pt-2 border-t border-primary">
            <span className="font-bold text-foreground">Balance Due:</span>
            <span className="font-bold text-primary text-lg w-32">{formatNaira(balance)}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-border text-center text-xs text-muted-foreground">
          <p>This invoice is generated by PropTRUST Navigator. For inquiries, contact your assigned lawyer.</p>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={handlePrint}><Printer className="h-4 w-4 mr-2" />Print Invoice</Button>
      </div>
    </div>
  );
}
